import React, { FC, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";

export interface SectionHeroProps {
  className?: string;
  rightImg: string;
  heading: ReactNode;
  subHeading: string;
  btnText: string;
}

const SectionHero: FC<SectionHeroProps> = ({
  className = "",
  rightImg,
  heading,
  subHeading,
  btnText,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div
      className={`nc-SectionHero relative ${className} mb-20 lg:mb-36`}
      data-nc-id="SectionHero"
    >
      <div className="flex flex-col lg:flex-row items-center relative text-center lg:text-left lg:justify-between gap-10">
        {/* Left Content */}
        <div className="flex-1 flex flex-col gap-8 max-w-2xl">
          <div>
            <h2 className="text-4xl !leading-tight font-semibold text-[#121212] md:text-5xl xl:text-5xl dark:text-neutral-100 mb-4 text-left">
              {heading}
            </h2>
            <span className="block !font-normal !text-lg text-[#121212B2] dark:text-neutral-400 text-left">
              {subHeading}
            </span>
          </div>
          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Feature 1 */}
            <div className="bg-primary-100 rounded-lg p-6 flex flex-col items-start shadow">
              <div className="mb-3 text-2xl">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M24 8.31987C24 12.8762 20.3063 16.5698 15.75 16.5698C15.2241 16.5698 14.7098 16.52 14.2112 16.426L13.0857 17.6922C12.9801 17.811 12.8506 17.906 12.7057 17.9711C12.5608 18.0362 12.4037 18.0698 12.2448 18.0698H10.5V19.9448C10.5 20.5662 9.99633 21.0698 9.375 21.0698H7.5V22.9448C7.5 23.5662 6.99633 24.0698 6.375 24.0698H1.125C0.503672 24.0698 0 23.5662 0 22.9448V19.2858C0 18.9874 0.118547 18.7013 0.329484 18.4903L7.91395 10.9058C7.64569 10.0924 7.5 9.22315 7.5 8.31982C7.5 3.76348 11.1936 0.0698711 15.75 0.0698242C20.3197 0.0697773 24 3.75003 24 8.31987ZM15.75 6.06982C15.75 7.31248 16.7573 8.31982 18 8.31982C19.2427 8.31982 20.25 7.31248 20.25 6.06982C20.25 4.82717 19.2427 3.81982 18 3.81982C16.7573 3.81982 15.75 4.82717 15.75 6.06982Z"
                    fill="white"
                  />
                </svg>
              </div>
              <div className="font-bold text-xl mb-2 text-white">
                Easy Access
              </div>
              <div className="text-white text-left">
                Enjoy hassle-free booking and pickup options tailored to your
                schedule—get on the road faster without any delays.
              </div>
            </div>
            {/* Feature 2 */}
            <div className="bg-[#F7F7F7] rounded-lg p-6 flex flex-col items-start shadow">
              <div className="mb-3 text-2xl">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21.8472 3.99331L12.8472 0.243314C12.573 0.129585 12.2791 0.0710449 11.9823 0.0710449C11.6855 0.0710449 11.3916 0.129585 11.1175 0.243314L2.11748 3.99331C1.27842 4.34019 0.72998 5.1605 0.72998 6.06988C0.72998 15.3746 6.09717 21.8058 11.1128 23.8964C11.6659 24.1261 12.2894 24.1261 12.8425 23.8964C16.8597 22.223 23.23 16.4433 23.23 6.06988C23.23 5.1605 22.6815 4.34019 21.8472 3.99331ZM11.9847 20.9902L11.98 3.13081L20.2253 6.56675C20.0706 13.6636 16.3769 18.8058 11.9847 20.9902Z"
                    fill="#FBBE06"
                  />
                </svg>
              </div>
              <div className="font-bold text-xl mb-2 text-[#121212]">
                Total Protection
              </div>
              <div className="text-[#888888] text-left">
                Drive with confidence. Our rentals include full insurance
                coverage and 24/7 roadside assistance to keep you protected.
              </div>
            </div>
            {/* Feature 3 */}
            <div className="bg-[#F7F7F7] rounded-lg p-6 flex flex-col items-start shadow">
              <div className="mb-3 text-2xl">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 0.444824C5.57812 0.444824 0.375 5.64795 0.375 12.0698C0.375 18.4917 5.57812 23.6948 12 23.6948C18.4219 23.6948 23.625 18.4917 23.625 12.0698C23.625 5.64795 18.4219 0.444824 12 0.444824ZM16.3355 15.1167L15.398 16.2886C15.3364 16.3655 15.2604 16.4295 15.1741 16.4771C15.0878 16.5246 14.993 16.5546 14.8951 16.5655C14.7972 16.5764 14.6982 16.5679 14.6036 16.5405C14.5089 16.5131 14.4207 16.4673 14.3438 16.4058L11.2031 14.0751C10.9837 13.8994 10.8066 13.6767 10.6848 13.4233C10.5631 13.1699 10.5 12.8923 10.5 12.6112V5.31982C10.5 5.12091 10.579 4.93015 10.7197 4.78949C10.8603 4.64884 11.0511 4.56982 11.25 4.56982H12.75C12.9489 4.56982 13.1397 4.64884 13.2803 4.78949C13.421 4.93015 13.5 5.12091 13.5 5.31982V12.0698L16.2188 14.062C16.2957 14.1236 16.3598 14.1997 16.4073 14.286C16.4548 14.3724 16.4848 14.4672 16.4957 14.5652C16.5065 14.6631 16.4979 14.7622 16.4705 14.8569C16.443 14.9515 16.3971 15.0398 16.3355 15.1167Z"
                    fill="#FBBE06"
                  />
                </svg>
              </div>
              <div className="font-bold text-xl mb-2 text-[#121212]">
                On-Time Always
              </div>
              <div className="text-[#888888] text-left">
                Punctuality is our promise. Your vehicle will be ready when and
                where you need it—no waiting, no worries.
              </div>
            </div>
            {/* Feature 4 */}
            <div className="bg-[#F7F7F7] rounded-lg p-6 flex flex-col items-start shadow">
              <div className="mb-3 text-2xl">
                <svg
                  width="24"
                  height="22"
                  viewBox="0 0 24 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7841 1.14478L8.06329 6.66145L1.97579 7.54895C0.884126 7.70728 0.446626 9.05312 1.23829 9.82395L5.64246 14.1156L4.60079 20.1781C4.41329 21.274 5.56746 22.0948 6.53413 21.5823L11.98 18.7198L17.4258 21.5823C18.3925 22.0906 19.5466 21.274 19.3591 20.1781L18.3175 14.1156L22.7216 9.82395C23.5133 9.05312 23.0758 7.70728 21.9841 7.54895L15.8966 6.66145L13.1758 1.14478C12.6883 0.161449 11.2758 0.148949 10.7841 1.14478Z"
                    fill="#FBBE06"
                  />
                </svg>
              </div>
              <div className="font-bold text-xl mb-2 text-[#121212]">
                Premium Service
              </div>
              <div className="text-[#888888] text-left">
                From top-tier vehicles to exceptional customer support, we
                deliver a five-star experience every time.
              </div>
            </div>
          </div>
        </div>
        {/* Right Content */}
        <div className="flex-1 flex flex-col items-start gap-6 max-w-xl">
          <img
            className="rounded-2xl w-full object-cover"
            src={rightImg}
            alt=""
          />
          {location.pathname !== "/about" && (
            <ButtonPrimary
              className="rounded-md"
              onClick={() => navigate("/about")}
            >
              {btnText}
            </ButtonPrimary>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionHero;
